import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { API, getToken } from "@/lib/api";
import { Calendar, Video, ShieldCheck, Clock, Check, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * /book — paid session booking page.
 *
 * Flow: User picks a tier ($79 / 30min or $149 / 60min) → backend creates
 * a Stripe Checkout session → we redirect → on success Stripe sends the
 * user back to /book/success?session_id=… which reveals the Calendly
 * scheduling link and the Doxy.me HIPAA video room URL.
 *
 * Prices live ONLY on the server — we never trust amounts coming back from
 * the browser (see Stripe playbook §1 Security Patterns).
 */
const TIERS = [
    {
        id: "30",
        price: "$79",
        label: "30-minute session",
        bestFor: "New to caregiving — getting your bearings, one big question, one clear next step.",
        bullets: [
            "Walk through your situation",
            "Pick your one next move",
            "A short follow-up email recap",
        ],
    },
    {
        id: "60",
        price: "$149",
        label: "60-minute session",
        bestFor: "Multiple things on the table — facility decisions, conflict in the family, planning ahead.",
        bullets: [
            "Deeper history + medication review",
            "Walk through the roadmap together",
            "Detailed written plan after the call",
        ],
        recommended: true,
    },
];

export default function Book() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(null); // tier id while redirecting
    const [email, setEmail] = useState(user?.email || "");

    const start = async (tierId) => {
        setLoading(tierId);
        try {
            const token = getToken();
            const res = await fetch(`${API}/booking/checkout`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    package_id: tierId,
                    origin_url: window.location.origin,
                    contact_email: email || null,
                }),
            });
            const data = await res.json();
            if (!res.ok || !data.checkout_url) {
                throw new Error(data.detail || "Could not start checkout");
            }
            // Hop to Stripe. The success_url Stripe redirects to is
            // /book/success?session_id=… on our origin.
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(String(e?.message || e));
            setLoading(null);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 py-10 md:py-16 animate-fade-in-up" data-testid="book-page">
            <Link to="/navigator" className="text-sm text-teal-dark hover:underline inline-flex items-center gap-1.5 mb-6">
                <ArrowLeft size={14} strokeWidth={1.8} /> Back to Care Navigator
            </Link>

            <header className="mb-10 max-w-3xl">
                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2 flex items-center gap-1.5">
                    <Calendar size={14} strokeWidth={1.8} /> Book a session with Ashlee
                </p>
                <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#12776F]">
                    Confidential 1:1 video with a Nurse Practitioner.
                </h1>
                <p className="mt-3 text-lg text-[#4C5551] leading-relaxed">
                    Sessions happen on Doxy.me — a HIPAA-compliant, in-browser video room. No app
                    install, no waiting room, no insurance billing.
                </p>
                <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-[#4C5551]">
                    <li className="inline-flex items-center gap-1.5"><Video size={14} strokeWidth={1.8} /> Doxy.me HIPAA video</li>
                    <li className="inline-flex items-center gap-1.5"><ShieldCheck size={14} strokeWidth={1.8} /> Encrypted, no recording</li>
                    <li className="inline-flex items-center gap-1.5"><Clock size={14} strokeWidth={1.8} /> Same-week availability</li>
                </ul>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                {TIERS.map((tier) => (
                    <article
                        key={tier.id}
                        data-testid={`book-tier-${tier.id}`}
                        className={`ml-card p-6 md:p-8 flex flex-col ${tier.recommended ? "ring-2 ring-teal !border-teal" : ""}`}
                    >
                        {tier.recommended && (
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-white bg-teal rounded-2xl px-2.5 py-0.5 self-start mb-3">
                                Most chosen
                            </p>
                        )}
                        <h2 className="font-heading text-2xl md:text-3xl text-[#12776F]">{tier.label}</h2>
                        <p className="mt-1 text-3xl md:text-4xl font-heading text-teal-dark">
                            {tier.price}<span className="text-base text-[#4C5551] font-normal ml-1">USD</span>
                        </p>
                        <p className="mt-3 text-[#4C5551] leading-relaxed text-sm">{tier.bestFor}</p>
                        <ul className="mt-4 space-y-2 text-sm text-[#1F2624] flex-1">
                            {tier.bullets.map((b, i) => (
                                <li key={i} className="flex items-start gap-2">
                                    <Check size={16} strokeWidth={2} className="text-teal mt-0.5 shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                        <button
                            data-testid={`book-checkout-${tier.id}`}
                            onClick={() => start(tier.id)}
                            disabled={loading !== null}
                            className="ml-btn-primary !min-h-[52px] mt-6 w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading === tier.id ? (
                                <><Loader2 size={18} strokeWidth={1.8} className="animate-spin" /> Redirecting to checkout…</>
                            ) : (
                                <>Book — {tier.price}</>
                            )}
                        </button>
                    </article>
                ))}
            </div>

            <section className="ml-card p-5 mt-8 bg-cream-light" data-testid="book-email">
                <label htmlFor="book-contact-email" className="text-sm font-semibold text-[#1F2624] block mb-1">
                    Best email to send your confirmation
                </label>
                <p className="text-xs text-[#4C5551] mb-2">
                    We&apos;ll send your Calendly scheduling link + the Doxy.me video room here as soon as payment clears.
                </p>
                <input
                    id="book-contact-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="book-contact-email"
                    className="ml-input !min-h-[48px] w-full"
                />
            </section>

            <section className="mt-10 text-sm text-[#4C5551] leading-relaxed max-w-3xl">
                <h3 className="font-heading text-base text-[#1F2624] mb-2">A few notes before you book</h3>
                <ul className="space-y-2 list-disc pl-5">
                    <li>This is <strong>not</strong> medical care. Ashlee is a Nurse Practitioner, but the session is for coaching, education, and care navigation — not diagnosis or prescribing.</li>
                    <li>If your loved one is in immediate danger, please call <strong>911</strong> or the <strong>988</strong> Suicide & Crisis Lifeline.</li>
                    <li>Need to reschedule or cancel? Use the link in your Calendly confirmation, or email <a href="mailto:ashlee@asvmedicalservices.com" className="text-teal-dark hover:underline">ashlee@asvmedicalservices.com</a> 24h before.</li>
                </ul>
            </section>
        </div>
    );
}
